import { useEffect, useState } from "react";
import { Icon } from "../../components/Icon";
import { formatDateTime } from "../../lib/orders";
import {
  useApprovePayment,
  usePaymentDetail,
  useRejectPayment,
} from "../../lib/usePaymentsVerification";
import { AI_VERDICT_LABELS_RU } from "./PaymentsVerificationLanding";

// A1.2 — receipt detail. Renders the uploaded receipt (image inline / PDF in
// an iframe) once a blob store exists, the AI verdict block, and the
// Approve / Reject actions. `fileUrl` is currently always null (no blob store
// yet — see paymentReview.service), so the preview degrades gracefully.

function ReceiptPreview({
  fileUrl,
  fileType,
  fileId,
}: {
  fileUrl: string | null;
  fileType: string;
  fileId: string;
}) {
  if (!fileUrl) {
    return (
      <div className="pv-preview pv-preview-empty">
        <span style={{ fontSize: 32 }}>🧾</span>
        <p>Превью недоступно — нет файлового хранилища</p>
        <p className="mono" style={{ fontSize: 11 }}>
          {fileType} · {fileId.slice(0, 16)}…
        </p>
      </div>
    );
  }
  if (fileType === "application/pdf") {
    return (
      <iframe
        className="pv-preview"
        title="Чек (PDF)"
        src={fileUrl}
        style={{ width: "100%", height: 360, border: 0 }}
      />
    );
  }
  return (
    <img
      className="pv-preview"
      alt="Чек"
      src={fileUrl}
      style={{ maxWidth: "100%", borderRadius: 8 }}
    />
  );
}

export function PaymentDetailDrawer({
  paymentId,
  onClose,
}: {
  paymentId: string;
  onClose: () => void;
}) {
  const { data: p, isLoading } = usePaymentDetail(paymentId);
  const approve = useApprovePayment();
  const reject = useRejectPayment();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const busy = approve.isPending || reject.isPending;
  // Зеркало серверного гейта cannot_approve_ai_failed (paymentReview.service):
  // без распознанной суммы approve зачёл бы 0 GEL. Reject остаётся доступен.
  const aiUnrecognized = !!p && p.aiPaidAmount == null;

  const onApprove = async () => {
    await approve.mutateAsync({ id: paymentId });
    onClose();
  };
  const onReject = async () => {
    if (!reason.trim()) return;
    await reject.mutateAsync({ id: paymentId, reason: reason.trim() });
    onClose();
  };

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-head">
          <button className="btn-admin ghost" onClick={onClose}>
            <Icon name="close" />
          </button>
          <h2>
            {isLoading
              ? "Загрузка…"
              : p
                ? "Проверка чека"
                : "Платёж не найден"}
          </h2>
        </div>

        {p && (
          <div className="drawer-body">
            <ReceiptPreview
              fileUrl={p.fileUrl}
              fileType={p.fileType}
              fileId={p.fileId}
            />

            <div className="pv-verdict">
              <div className="pv-verdict-head">
                <Icon name="alert" size={14} />
                <span className="bold">
                  AI-вердикт:{" "}
                  {p.aiVerdict
                    ? AI_VERDICT_LABELS_RU[p.aiVerdict]
                    : "не определён"}
                </span>
              </div>
              <dl className="pv-kv">
                <div>
                  <dt>Распознано</dt>
                  <dd className="tnum">
                    {p.aiPaidAmount != null ? `${p.aiPaidAmount} GEL` : "—"}
                  </dd>
                </div>
                <div>
                  <dt>Цель AI</dt>
                  <dd className="tnum">
                    {p.aiTargetAmount != null
                      ? `${p.aiTargetAmount} GEL`
                      : "—"}
                  </dd>
                </div>
                <div>
                  <dt>К оплате (заказ)</dt>
                  <dd className="tnum">
                    {p.totalAmount != null ? `${p.totalAmount} GEL` : "—"}
                  </dd>
                </div>
                <div>
                  <dt>Статус заказа</dt>
                  <dd>{p.orderStatus}</dd>
                </div>
                <div>
                  <dt>Загружен</dt>
                  <dd>{formatDateTime(p.createdAt)}</dd>
                </div>
              </dl>
              {p.aiMessage && (
                <p className="pv-ai-msg muted">{p.aiMessage}</p>
              )}
            </div>

            {rejecting ? (
              <div className="pv-reject">
                <textarea
                  placeholder="Причина отклонения для клиента"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                />
                <div className="pv-actions">
                  <button
                    className="btn-admin"
                    disabled={busy}
                    onClick={() => setRejecting(false)}
                  >
                    Отмена
                  </button>
                  <button
                    className="btn-admin danger"
                    disabled={busy || !reason.trim()}
                    onClick={onReject}
                  >
                    Отклонить чек
                  </button>
                </div>
              </div>
            ) : (
              <div className="pv-actions">
                <button
                  className="btn-admin danger"
                  disabled={busy}
                  onClick={() => setRejecting(true)}
                >
                  Отклонить
                </button>
                <button
                  className="btn-admin primary"
                  disabled={busy || aiUnrecognized}
                  title={
                    aiUnrecognized
                      ? "Чек не распознан AI — отклоните его и попросите клиента загрузить заново"
                      : undefined
                  }
                  onClick={onApprove}
                >
                  <Icon name="check" size={14} /> Подтвердить
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
